import JSZip from "jszip";
import { drawLabelToCanvas, generateQRCodeDataUrl, preloadLogoImage } from "@/lib/label-utils";

/**
 * Generate a ZIP file containing QR sticker PNGs for a set of boxes.
 * Reusable across AdminAddPrepaid wizard and UserDetailSheet.
 */
export async function generateQRStickerZip(
  boxes: Array<{ box_number: string }>,
  appUrl?: string
): Promise<{ blob: Blob; elapsed: string }> {
  const startTime = performance.now();
  const origin = appUrl || window.location.origin;

  console.log("[QR ZIP] Starting generation for", boxes.length, "boxes");

  const zip = new JSZip();
  const qrFolder = zip.folder("qr-stickers");

  // Pre-load logo once
  const logoUrl = `${origin}/endless-storage-logo.png`;
  const logoImg = await preloadLogoImage(logoUrl);

  // Process in parallel batches of 5
  const BATCH_SIZE = 5;
  const results: Array<{ boxNumber: string; base64: string }> = [];

  for (let batchStart = 0; batchStart < boxes.length; batchStart += BATCH_SIZE) {
    const batch = boxes.slice(batchStart, batchStart + BATCH_SIZE);

    const batchPromises = batch.map(async (box) => {
      const qrUrl = `${origin}/qr/${box.box_number}`;
      const qrDataUrl = await generateQRCodeDataUrl(qrUrl);
      const labelDataUrl = await drawLabelToCanvas(box.box_number, qrDataUrl, logoImg);
      const base64 = labelDataUrl.split(",")[1];
      return { boxNumber: box.box_number, base64 };
    });

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
  }

  for (const { boxNumber, base64 } of results) {
    qrFolder?.file(`${boxNumber}.png`, base64, { base64: true });
  }

  const blob = await zip.generateAsync({ type: "blob" });
  const elapsed = ((performance.now() - startTime) / 1000).toFixed(1);
  console.log(`[QR ZIP] Complete in ${elapsed}s, size: ${(blob.size / 1024).toFixed(2)} KB`);

  return { blob, elapsed };
}
